// Diagnóstico da VPS (só o admin da plataforma): servidor, banco, ingestão, broker,
// certificados e arquivos, cada um com estado próprio. Atualiza a cada 30 s.

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CircleX, OctagonAlert, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { Badge, Button, Card, PageHeader, Spinner, cx } from "../../components/ui";
import { api } from "../../lib/api";
import { ALERT_TITLE, usePlatformAlerts } from "../../components/PlatformAlerts";
import { AdminTrash } from "./AdminTrash";

type Level = "ok" | "warn" | "crit" | "error";

interface Check {
  key: string;
  title: string;
  level: Level;
  summary: string;
  details?: any;
}

interface Diagnostics {
  at: string;
  tookMs: number;
  overall: Level;
  checks: Check[];
}

// Estado sempre com ícone + texto: nunca só a cor.
const LEVEL: Record<Level, { label: string; tone: "ok" | "warn" | "danger"; icon: typeof CheckCircle2 }> = {
  ok: { label: "ok", tone: "ok", icon: CheckCircle2 },
  warn: { label: "atenção", tone: "warn", icon: AlertTriangle },
  crit: { label: "crítico", tone: "danger", icon: OctagonAlert },
  error: { label: "falhou", tone: "danger", icon: CircleX },
};

function LevelBadge({ level }: { level: Level }) {
  const L = LEVEL[level];
  return (
    <Badge tone={L.tone}>
      <L.icon className="size-3" aria-hidden /> {L.label}
    </Badge>
  );
}

const fmt = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
const age = (s: number | null) =>
  s === null || s === undefined ? "—" : s < 120 ? `${s} s` : s < 7200 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`;

/** Barra de uso: preenchimento com a severidade, trilho neutro (medidor). */
function Meter({ label, used, total, unit }: { label: string; used: number; total: number; unit: string }) {
  const pct = total ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs">
        <span>{label}</span>
        <span className="text-muted tabular-nums">
          {used} / {total} {unit} · {Math.round(pct)}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-surface-2" role="meter" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
        <div className={cx("h-full rounded-full", pct > 95 ? "bg-danger" : pct > 85 ? "bg-warn" : "bg-brand")} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Rows({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="text-muted">{k}</dt>
          <dd className="min-w-0 break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Details({ check: c }: { check: Check }) {
  const d = c.details;
  if (!d) return null;
  switch (c.key) {
    case "server":
      return (
        <div className="space-y-3">
          <Meter label="Disco" used={d.diskGb.used} total={d.diskGb.total} unit="GB" />
          <Meter label="Memória" used={d.memoryGb.used} total={d.memoryGb.total} unit="GB" />
          <Rows
            rows={[
              ["Carga (1/5/15 min)", `${d.load.join(" / ")} · ${d.cpus} CPU`],
              ["Servidor ligado há", `${d.uptimeHours} h`],
              ["API no ar há", `${d.apiUptimeHours} h · Node ${d.node}`],
            ]}
          />
        </div>
      );
    case "database":
      return (
        <div className="space-y-3">
          <Rows
            rows={[
              ["Extensões", d.extensions.map((e: any) => `${e.extname} ${e.extversion}`).join(", ")],
              ["Última migração", d.lastMigration ? `${d.lastMigration.name} (${fmt(d.lastMigration.applied_at)})` : "—"],
              ...(d.timescaleJobs
                ? ([
                    ["Chunks de medições", `${d.timescaleJobs.chunks} (${d.timescaleJobs.compressedChunks} comprimidos)`],
                    ["Tarefas do Timescale", `${d.timescaleJobs.total} (${d.timescaleJobs.failing} falhando)`],
                  ] as [string, string][])
                : []),
            ]}
          />
          <div>
            <p className="mb-1 text-xs font-medium">Maiores tabelas</p>
            <ul className="space-y-0.5 text-xs">
              {d.largest.map((t: any) => (
                <li key={t.name} className="flex justify-between gap-2">
                  <code className="font-mono">{t.name}</code>
                  <span className="text-muted tabular-nums">{t.size}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    case "ingest":
      return (
        <div className="space-y-3">
          <ul className="space-y-1 text-xs">
            {d.regulators.map((r: any) => (
              <li key={r.id} className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate">
                  {r.site} › {r.name}
                </span>
                <span className={cx("shrink-0 tabular-nums", r.online && r.age_s > 300 ? "text-warn" : "text-muted")}>
                  {r.online ? "online" : r.online === false ? "sem link" : "sem dados"} · {age(r.age_s)}
                </span>
              </li>
            ))}
          </ul>
          <Rows rows={[["Recusadas em 24 h", d.ingestErrors24h], ["Anomalias em 24 h", d.anomalies24h]]} />
          {d.lastErrors.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium">Últimas recusadas</p>
              <ul className="space-y-1 text-xs">
                {d.lastErrors.map((e: any, i: number) => (
                  <li key={i}>
                    <span className="text-muted">{fmt(e.received_at)}</span> <code className="font-mono">{e.topic}</code>
                    <p className="text-muted">{e.error}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    case "broker":
      return (
        <ul className="space-y-1 text-xs">
          {d.clients.map((cl: any) => (
            <li key={cl.username} className="flex items-center justify-between gap-2">
              <code className="min-w-0 truncate font-mono">{cl.username}</code>
              <span className={cx("shrink-0", cl.connected ? "text-ok" : "text-muted")}>
                {cl.connected ? `conectado${cl.addresses.length ? ` · ${cl.addresses.join(", ")}` : ""}` : "desconectado"}
              </span>
            </li>
          ))}
        </ul>
      );
    case "certificates":
      return (
        <ul className="space-y-2 text-xs">
          {d.map((r: any) => (
            <li key={r.host}>
              <p>
                <code className="font-mono">
                  {r.host}:{r.port}
                </code>{" "}
                <span className="text-muted">({r.role})</span>
              </p>
              <p className={r.error ? "text-danger" : "text-muted"}>
                {r.error ? `falhou: ${r.error}` : `vale até ${fmt(r.validTo)} · ${r.daysLeft} dias · ${r.issuer}`}
              </p>
            </li>
          ))}
        </ul>
      );
    case "files":
      return (
        <div className="space-y-4">
          <Rows
            rows={d.map((f: any) => [f.kind === "osc" ? "Oscilografias" : f.kind === "evt" ? "Eventos" : f.kind, `${f.n} arquivos · ${f.size} brutos · ${f.trash} na lixeira`])}
          />
          <AdminTrash />
        </div>
      );
    default:
      return <pre className="overflow-auto text-xs">{JSON.stringify(d, null, 2)}</pre>;
  }
}

/** Histórico dos alarmes da plataforma (broker fora), abertos primeiro. */
function AlertHistory() {
  const { data } = usePlatformAlerts(true);
  if (!data?.recent.length) return null;
  return (
    <Card className="mb-4 p-4">
      <h2 className="mb-2 font-semibold">Alarmes da plataforma</h2>
      <ul className="space-y-2 text-sm">
        {data.recent.map((a) => (
          <li key={a.id} className="flex flex-wrap items-start gap-x-3 gap-y-0.5">
            {a.cleared_at ? <Badge tone="ok">normalizado</Badge> : <Badge tone="danger">aberto</Badge>}
            <div className="min-w-0 flex-1">
              <p className={cx(!a.cleared_at && "font-medium")}>{ALERT_TITLE[a.kind] ?? a.kind}</p>
              <p className="text-xs text-muted">{a.summary}</p>
            </div>
            <p className="text-xs text-muted tabular-nums">
              {fmt(a.raised_at)} → {a.cleared_at ? fmt(a.cleared_at) : "agora"}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function DiagnosticsPage() {
  const q = useQuery({
    queryKey: ["diagnostics"],
    queryFn: () => api.get<Diagnostics>("/api/admin/diagnostics"),
    refetchInterval: 30_000,
  });
  const d = q.data;

  return (
    <>
      <PageHeader
        title="Diagnóstico"
        description="Saúde da instalação: servidor, banco, ingestão, broker, certificados e arquivos."
        actions={
          <Button variant="secondary" onClick={() => q.refetch()} loading={q.isFetching}>
            <RefreshCw className="size-4" aria-hidden /> Atualizar
          </Button>
        }
      />
      {q.isLoading || !d ? (
        <Spinner />
      ) : (
        <>
          <Card className="mb-4 flex flex-wrap items-center gap-3 p-4">
            <LevelBadge level={d.overall} />
            <p className="text-sm">
              {d.overall === "ok"
                ? "Tudo em ordem."
                : `${d.checks.filter((c) => c.level !== "ok").length} verificação(ões) pedem atenção.`}
            </p>
            <p className="ml-auto text-xs text-muted">
              {fmt(d.at)} · {d.tookMs} ms · atualiza a cada 30 s
            </p>
          </Card>
          <AlertHistory />
          <div className="grid gap-4 lg:grid-cols-2">
            {d.checks.map((c) => (
              <Card key={c.key} className={cx("p-4", c.key === "files" && "lg:col-span-2", c.level === "crit" || c.level === "error" ? "border-danger/50" : c.level === "warn" && "border-warn/50")}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <h2 className="font-semibold">{c.title}</h2>
                  <LevelBadge level={c.level} />
                </div>
                <p className="mb-3 text-sm text-muted">{c.summary}</p>
                <Details check={c} />
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );
}
